import React from 'react';
import PropTypes from 'prop-types';
import Grid from '../Grid';
import GridItem from '../GridItem';
import CloseIcon from 'shared-vectors/icons/CloseL';
import ModalBack from 'shared-vectors/icons/ModalBack';
import GalleryArrow from 'shared-vectors/icons/GalleryArrow';
import { Icon } from 'shared-vectors';
import createStyle from 'shared-utils/src/createStyle';
import sleep from 'shared-utils/src/sleep';
import makeClassName from 'shared-utils/src/makeClassName';
import trackGTMEvents from 'shared-utils/src/trackGTMEvents/new';
import styles, { mainCls } from './styles';

const useStyles = createStyle(styles);

export default function ModalHeader({
  className,
  title,
  compact,
  noShadow,
  data,
  version,
  backButton,
  backButtonAndClose,
  customCloseAction,
  trackOnClose,
  trackOnBack,
}) {
  const classes = useStyles();
  const clsMain = version > 1
    ? makeClassName([
      className,
      classes[`${mainCls}--DS`],
      compact || data.headerCompact ? `${classes[`${mainCls}--scrl`]} bb--s` : 0,
      classes[`${mainCls}--noshdw`],
      classes[mainCls],
    ]) : makeClassName([
      className,
      compact || data.headerCompact ? classes[`${mainCls}--scrl`] : 0,
      noShadow ? classes[`${mainCls}--noshdw`] : 0,
      classes[mainCls],
    ]);

  const clsTitle = version > 1
    ? makeClassName([
      'tp-s--l tp-w--m tp--ell tp-a--c c-txt--f0',
      classes[`${mainCls}__DStitle`],
      compact || data.headerCompact ? `${classes[`${mainCls}__DStitle--vis`]} is-anim` : classes[`${mainCls}__DStitle--hid`],
    ]) : makeClassName([
      classes[`${mainCls}__title`],
      'tp--ell tp--b c-txt--secondary',
    ]);

  const clsAction = makeClassName([
    'is-clickable',
    version > 1 && classes[`${mainCls}__action`],
  ]);

  const clsActionNoClick = makeClassName([
    version > 1 && classes[`${mainCls}__action`],
  ]);

  const closeAction = () => {
    if (customCloseAction) {
      if (trackOnClose) {
        const { defaultDimension, moreOption, debug } = trackOnClose;
        sleep(300).then(() => trackGTMEvents(defaultDimension, moreOption, debug));
      }
      customCloseAction();
    } else {      
      data.activeClose();
    }
  };

  const backAction = () => {
    if(trackOnBack) {
      const { defaultDimension, moreOption, debug } = trackOnBack;
      sleep(300).then(() => trackGTMEvents(defaultDimension, moreOption, debug));
    }
    backButton.action();
  }
  
  return (
    <GridItem className={clsMain} behavior="fixed">
      <Grid align={version === 2 ? 'flex-start' : 'center'}>
        {
          backButton
            ? (
              <GridItem className={clsAction} behavior="fixed" onClick={backAction}>
                {
                  version > 1
                    ? <Icon className={classes[`${mainCls}__action--back`]} name={ModalBack} color="secondary" />
                    : <Icon name={GalleryArrow} color="secondary" />
                }
              </GridItem>
            ) : <GridItem className={clsActionNoClick} behavior="fixed" />
        }
        <GridItem className={clsTitle}>
          {title}
        </GridItem>
        {
          (!backButton || backButtonAndClose)
            ? (
              <GridItem className={clsAction} behavior="fixed" onClick={closeAction}>
                {
                  version > 1
                    ? <Icon name={CloseIcon} color="secondary" size="m" />
                    : <Icon name={CloseIcon} color="secondary" />
                }
              </GridItem>
            ) : <GridItem className={clsActionNoClick} behavior="fixed" />

        }
      </Grid>
    </GridItem>
  )
};

ModalHeader.propTypes = {
  /**
   * Component class
   */
  className: PropTypes.string,

  /**
   * Modal title
   */
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),

  /**
   * Compact version of the header
   */
  compact: PropTypes.bool,

  /**
   * No shadow for compact header
   */
  noShadow: PropTypes.bool,

  /**
   * Callback data passed from the Modal (eg. contain the function to trigger the close)
   */
  data: PropTypes.object,

  /**
   * New standard layout
   */
  version: PropTypes.number,

  backButton: PropTypes.object,

  customCloseAction: PropTypes.func,
};

ModalHeader.defaultProps = {
  className: '',
  title: '',
  compact: false,
  noShadow: false,
  data: {},
  version: 1,
  backButton: null,
  customCloseAction: null,
};
